import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { User } from '../models/user.model';
import { AwsUser } from '../models/aws-user.model';
import { AppConfigService } from '../core/services/app-config.service';
import { TokenStorageService } from './authentication/token-storage.service';
import { SessionService } from './authentication/session.service';
import { Config } from '../models/config.model';
import { signIn, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword, confirmSignIn } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';

@Injectable({ providedIn: 'root' })
export class AwsService {
  private isConfigured = false;

  constructor(
    private readonly appConfigService: AppConfigService,
    private readonly tokenStorageService: TokenStorageService,
    private readonly storage: SessionService,
  ) {
    this.initializeAmplify();
  }

  private async initializeAmplify(): Promise<void> {
    try {
      const config = await this.appConfigService.getConfig().toPromise();
      if (config) {
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: config.userPoolId,
              userPoolClientId: config.clientId
            }
          }
        });
        this.isConfigured = true;
      }
    } catch {
      // Configuration failed silently - will retry on next auth attempt
    }
  }

  getAwsConfig(): Observable<Config> {
    return this.appConfigService.getConfig();
  }

  setCognitoCreds(_creds: unknown): void {
    // Reserved for future use
  }

  getCognitoCreds(): unknown {
    return null;
  }

  buildCognitoCreds(_idTokenJwt: string, _poolData: { UserPoolId: string; ClientId: string }, _region: string, _identityPool: string): void {
    // Reserved for future use
  }

  getUsers(): Observable<User[]> {
    return of([]);
  }

  createUser(user: User): Observable<AwsUser> {
    return from(this.createUserAsync(user));
  }

  private async createUserAsync(user: User): Promise<AwsUser> {
    throw new Error('Not implemented');
  }

  editUser(user: User, isAdmin?: boolean): Observable<User> {
    return of(user);
  }

  deleteUser(user: User): Observable<User> {
    return of(user);
  }

  resendTemporaryPassword(username: string): Observable<User[]> {
    return of([]);
  }

  authenticate(username: string, password: string): Observable<void> {
    return from(this.authenticateAsync(username, password)).pipe(
      tap(() => this.checkInitialAuthState()),
      catchError(error => {
        throw error;
      })
    );
  }

  private async authenticateAsync(username: string, password: string): Promise<void> {
    if (!this.isConfigured) {
      await this.initializeAmplify();
    }

    try {
      const existingSession = await fetchAuthSession();
      if (existingSession.tokens?.accessToken) {
        const accessToken = existingSession.tokens.accessToken.toString();
        const idToken = existingSession.tokens.idToken?.toString() || '';
        
        localStorage.setItem('accessToken', accessToken);
        this.tokenStorageService.setIdToken(idToken);
        return;
      }
    } catch {
    }

    const result = await signIn({ username, password });
    
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      localStorage.setItem('tempUsername', username);
      localStorage.setItem('tempSignInResult', JSON.stringify(result));
      throw { reason: 'FORCE_CHANGE_PASSWORD', challengeName: 'NEW_PASSWORD_REQUIRED' };
    }

    if (result.isSignedIn) {
      const session = await fetchAuthSession();
      const tokens = session.tokens;
      
      if (tokens) {
        const accessToken = tokens.accessToken?.toString() || '';
        const idToken = tokens.idToken?.toString() || '';
        
        localStorage.setItem('accessToken', accessToken);
        this.tokenStorageService.setIdToken(idToken);
      }
    }
  }

  private async checkInitialAuthState(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        const accessToken = session.tokens.accessToken.toString();
        const idToken = session.tokens.idToken?.toString() || '';
        
        localStorage.setItem('accessToken', accessToken);
        if (idToken) {
          this.tokenStorageService.setIdToken(idToken);
        }
      }
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
    }
  }

  isLoggedIn(): Observable<boolean> {
    return from(this.isLoggedInAsync());
  }

  private async isLoggedInAsync(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch {
      return false;
    }
  }

  /**
   * Utilise le refresh token Cognito (géré par Amplify) pour obtenir un nouvel idToken.
   */
  refreshIdToken(): Observable<string> {
    return from(this.refreshIdTokenAsync());
  }

  private async refreshIdTokenAsync(): Promise<string> {
    if (!this.isConfigured) {
      await this.initializeAmplify();
    }

    const session = await fetchAuthSession({ forceRefresh: true });
    const tokens = session.tokens;
    const idToken = tokens?.idToken?.toString();

    if (tokens && idToken) {
      localStorage.setItem('accessToken', tokens.accessToken?.toString() || '');
      this.tokenStorageService.setIdToken(idToken);
      return idToken;
    }
    throw new Error('Failed to refresh tokens');
  }

  changePassword(username: string, oldPassword: string, newPassword: string): Observable<void> {
    return from(this.changePasswordAsync(username, oldPassword, newPassword));
  }

  private async changePasswordAsync(username: string, oldPassword: string, newPassword: string): Promise<void> {
    throw new Error('Not implemented - use completeNewPasswordChallenge');
  }

  resetPassword(username: string): Observable<void> {
    return from(this.resetPasswordAsync(username));
  }

  private async resetPasswordAsync(username: string): Promise<void> {
    await resetPassword({ username });
  }

  confirmResetPassword(username: string, confirmationCode: string, newPassword: string): Observable<void> {
    return from(this.confirmResetPasswordAsync(username, confirmationCode, newPassword));
  }

  private async confirmResetPasswordAsync(username: string, confirmationCode: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username, confirmationCode, newPassword });
  }

  completeNewPasswordChallenge(newPassword: string): Observable<{ accessToken: string; idToken: string }> {
    return from(this.completeNewPasswordChallengeAsync(newPassword));
  }

  private async completeNewPasswordChallengeAsync(newPassword: string): Promise<{ accessToken: string; idToken: string }> {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    
    if (result.isSignedIn) {
      const session = await fetchAuthSession();
      const tokens = session.tokens;
      
      if (tokens) {
        const accessToken = tokens.accessToken?.toString() || '';
        const idToken = tokens.idToken?.toString() || '';
        
        localStorage.setItem('accessToken', accessToken);
        this.tokenStorageService.setIdToken(idToken);
        
        localStorage.removeItem('tempUsername');
        localStorage.removeItem('tempSignInResult');
        
        return { accessToken, idToken };
      }
    }
    throw new Error('Failed to complete new password challenge');
  }

  logout(): Observable<void> {
    return from(this.logoutAsync());
  }

  private async logoutAsync(): Promise<void> {
    await signOut();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  getCurrentUser(): Observable<{ username: string; email: string }> {
    return from(this.getCurrentUserAsync());
  }

  private async getCurrentUserAsync(): Promise<{ username: string; email: string }> {
    const user = await getCurrentUser();
    return {
      username: user.username,
      email: user.signInDetails?.loginId || ''
    };
  }
}